import { css } from '@emotion/react';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FluentCloudDownload24Regular from '~icons/fluent/cloud-download-24-regular';

import {
	ButtonCancel,
	ButtonRow,
	ButtonWithLoading,
} from '@adflow/frontend/ui-buttons';
import { HubTooltip, InfoCircle } from '@adflow/frontend/ui-common';
import {
	DataTableFull,
	DataTableMetaProvider,
	DataTableSelectorCheckbox,
	DataTableSelectorMenu,
	HeaderCell,
	TruncatedTextCell,
	useColumnTemplate,
	useDataTableMeta,
} from '@adflow/frontend/ui-data-table';
import { HubAutocompleteWithControl } from '@adflow/frontend/ui-form';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { ModalDialog, ModalWithLayout } from '@adflow/frontend/ui-modal';
import { su, theme, typography } from '@adflow/frontend/ui-theme';
import { useAbility } from '@adflow/frontend/util-auth';
import { useWindowViewWidth } from '@adflow/frontend/util-common';
import {
	type SalesforceImportPreviewRow,
	StartSalesforceImportBody,
} from '@adflow/shared/data-salesforce-import';
import { formatDate } from '@adflow/shared/util-formatting';

import {
	useSalesforceImportPreview,
	useSalesforceImportSelectionOptions,
	useSalesforceStartImportMutation,
} from './queries';

const FormSchema = StartSalesforceImportBody;
const currencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});
const columnHelper = createColumnHelper<SalesforceImportPreviewRow>();

const styles = {
	title: css(typography.title, { marginBottom: '1.25rem' }),
	modalFooter: css(su.spaceX('1.25rem'), {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
	}),
	checkboxContainer: css({
		display: 'flex',
		'& > *': css({ alignSelf: 'center' }),
		'& > label': css({ display: 'flex', marginLeft: '0.5rem' }),
	}),
	salesforceImportContainer: css({
		display: 'flex',
		flexDirection: 'column',
		width: '1128px',
		maxWidth: '1128px',
		gap: '1rem',
	}),
	salesforceImportTitle: css({
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		fontSize: '24px',
	}),
	importIconContainer: (missingPermission?: boolean) =>
		css({
			paddingLeft: '0.75rem',
			paddingRight: '0.75rem',
			display: 'flex',
			alignItems: 'center',
			cursor: 'pointer',
			...(missingPermission && {
				cursor: 'not-allowed',
				color: theme.colors.text.disabled,
			}),
		}),
	importIcon: css({
		width: '1.5em',
		height: '1.5em',
	}),
	labelContainer: css({ alignSelf: 'center', paddingLeft: '0.25rem' }),
	selectorsContainer: css({
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: '1rem',
	}),
	previewCard: css({
		border: `1px solid ${theme.colors.input.border}`,
		borderRadius: '0.5rem',
		background: theme.colors.background.paper,
		overflow: 'hidden',
	}),
	previewHeader: css({
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: '0.75rem 1rem',
		borderBottom: `1px solid ${theme.colors.input.border}`,
		background: theme.colors.background.form,
	}),
	previewTitle: css(su.textSm, {
		fontWeight: 600,
	}),
	previewSummary: css(su.textXs, {
		color: theme.colors.text.secondary,
	}),
	previewEmptyState: css({
		padding: '1.5rem 1rem',
		color: theme.colors.text.secondary,
	}),
	errorText: css(su.textSm, {
		color: theme.colors.palettes.error.main,
	}),
	previewLoading: css({
		height: '220px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	}),
	previewTableWrap: css({
		height: '420px',
	}),
	adAccountTooltip: css({
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
		maxWidth: '360px',
		whiteSpace: 'normal',
	}),
	adAccountTooltipRow: css(su.textSm, {
		color: theme.colors.text.primary,
	}),
	archived: css({
		color: theme.colors.text.archivedRow,
		fontStyle: 'italic',
	}),
};

function joinValues(values: readonly string[]) {
	return values.filter(Boolean).join(', ');
}

function getCampaignNameSuffix(platformCampaignName: string) {
	const [_prefix, ...rest] = platformCampaignName.split('::');
	const suffix = rest.join('::').trim();

	return suffix || platformCampaignName;
}

function PreviewAdAccountsCell({
	adAccounts,
}: Pick<SalesforceImportPreviewRow, 'adAccounts'>) {
	const { t } = useTranslation('salesforce-import');

	const [showDialog, setShowDialog] = useState(false);

	const onClose = () => setShowDialog(false);

	const width = useWindowViewWidth();

	const calcColumns = useMemo(() => {
		const dataLength = adAccounts.length;

		// Limit columns to screen width / 300px
		const columnsPossible = Math.ceil(width / 300);

		const targetColumns = Math.ceil(dataLength / 12);

		// Use the smallest number of columns possible
		return Math.min(targetColumns, columnsPossible, 12);
	}, [adAccounts.length, width]);

	return (
		<>
			<div role="button" onClick={() => setShowDialog(true)}>
				<TruncatedTextCell
					value={String(adAccounts.length)}
					calloutText={t('showAll')}
				/>
			</div>
			<ModalDialog
				type="info"
				headerContent={t('adAccounts')}
				bodyContent={
					<ul
						css={{
							columnCount: calcColumns,
							columnWidth: 'auto',
							columnGap: '28px',
							listStyleType: 'disc',
							marginLeft: '1rem',
							marginRight: '1rem',
							marginBottom: '1rem',
						}}
					>
						{adAccounts.map((account) => (
							<li
								key={account.avbId}
								css={[su.textSm, { marginBottom: '0.25rem' }]}
							>
								{account.dailySpend === null
									? (account.name ?? account.avbId)
									: `${account.name ?? account.avbId} - ${currencyFormatter.format(
											account.dailySpend
										)}/day`}
							</li>
						))}
					</ul>
				}
				dialogStyleOverrides={{
					minWidth: '200px',
					maxWidth: '100%',
					marginLeft: '1rem',
					marginRight: '1rem',
				}}
				show={showDialog}
				onConfirm={onClose}
				onCancel={onClose}
			/>
		</>
	);
}

function usePreviewColumns({
	isRowDisabled,
}: {
	isRowDisabled: (row: SalesforceImportPreviewRow) => boolean;
}) {
	const { t } = useTranslation(['salesforce-import', 'common']);

	return useMemo<ColumnDef<SalesforceImportPreviewRow>[]>(
		() => [
			columnHelper.display({
				id: 'select',
				meta: {
					name: t('preview.columns.select'),
					sticky: false,
					disableOrdering: true,
					disableConfig: true,
					disableOverflow: true,
				},
				size: 60,
				enableSorting: false,
				enableResizing: false,
				header: ({ table }) => (
					<DataTableSelectorMenu
						items={table.getCenterRows().map((row) => row.original)}
						getId={(item) => item.id}
						isDisabled={isRowDisabled}
						options={[
							{
								content: t('common:buttons.clearSelection'),
								onSelect: ({ setSelected }) => setSelected([]),
								key: 'clear-selection',
								isVisible: true,
								isLink: true,
							},
						]}
					/>
				),
				cell: ({ row }) => {
					const disabled = isRowDisabled(row.original);

					return (
						<DataTableSelectorCheckbox
							item={row.original}
							getId={(item) => item.id}
							disabled={disabled}
						/>
					);
				},
			}),
			columnHelper.accessor((row) => joinValues(row.programTypes), {
				id: 'programTypes',
				meta: {
					name: t('preview.columns.programType'),
					sticky: false,
				},
				size: 170,
				header: HeaderCell,
				cell: ({ getValue }) => (
					<TruncatedTextCell value={getValue()} calloutText={getValue()} />
				),
				enableSorting: false,
			}),
			columnHelper.accessor((row) => joinValues(row.brands), {
				id: 'brands',
				meta: {
					name: t('preview.columns.brands'),
					sticky: false,
				},
				size: 80,
				header: HeaderCell,
				cell: ({ getValue }) => <TruncatedTextCell value={getValue()} />,
				enableSorting: false,
			}),
			columnHelper.accessor(
				(row) => getCampaignNameSuffix(row.platformCampaignName),
				{
					id: 'platformCampaignName',
					meta: {
						name: t('preview.columns.platformCampaignName'),
						sticky: false,
					},
					size: 300,
					header: HeaderCell,
					cell: ({ getValue }) => (
						<TruncatedTextCell value={getValue()} calloutText={getValue()} />
					),
					enableSorting: false,
				}
			),
			columnHelper.display({
				id: 'adAccounts',
				meta: {
					name: t('preview.columns.adAccounts'),
				},
				size: 80,
				header: HeaderCell,
				cell: ({ row }) => (
					<PreviewAdAccountsCell adAccounts={row.original.adAccounts} />
				),
				enableSorting: false,
			}),
			columnHelper.accessor('runStartDate', {
				id: 'runStartDate',
				meta: {
					name: t('preview.columns.runStartDate'),
				},
				size: 80,
				header: HeaderCell,
				cell: ({ getValue }) => (
					<TruncatedTextCell value={formatDate(getValue(), false)} />
				),
				enableSorting: false,
			}),
			columnHelper.accessor('runEndDate', {
				id: 'runEndDate',
				meta: {
					name: t('preview.columns.runEndDate'),
				},
				size: 80,
				header: HeaderCell,
				cell: ({ getValue }) => (
					<TruncatedTextCell value={formatDate(getValue(), false)} />
				),
				enableSorting: false,
			}),
			columnHelper.accessor((row) => joinValues(row.productCodes), {
				id: 'productCodes',
				meta: {
					name: t('preview.columns.productCode'),
				},
				size: 120,
				header: HeaderCell,
				cell: ({ getValue }) => (
					<TruncatedTextCell value={t(`productCode.${getValue()}`)} />
				),
				enableSorting: false,
			}),
		],
		[t, isRowDisabled]
	);
}

export function SalesforceImport() {
	return (
		<DataTableMetaProvider key="salesforce-import-preview">
			<SalesforceImportContent />
		</DataTableMetaProvider>
	);
}

function SalesforceImportContent() {
	const { t } = useTranslation('salesforce-import');
	const [show, setShow] = useState(false);
	const { ability, missingPermissionText } = useAbility();
	const { mutateAsync: startImport, isPending: importLoading } =
		useSalesforceStartImportMutation();
	const {
		data: selectionOptions,
		isLoading: selectionOptionsLoading,
		isError: selectionOptionsError,
	} = useSalesforceImportSelectionOptions(undefined, show);

	const formMethods = useForm({
		resolver: superstructResolver(FormSchema, {
			coerce: true,
			mask: true,
		}),
		defaultValues: {
			onGroup: '',
			onCampaignName: '',
			importFacebook: true,
			importGoogle: true,
		},
	});

	const { handleSubmit, setValue } = formMethods;
	const selectedGroup = formMethods.watch('onGroup');
	const selectedCampaignName = formMethods.watch('onCampaignName');
	const importFacebook = formMethods.watch('importFacebook');
	const importGoogle = formMethods.watch('importGoogle');
	const {
		data: campaignSelectionOptions,
		isLoading: campaignSelectionOptionsLoading,
		isError: campaignSelectionOptionsError,
	} = useSalesforceImportSelectionOptions(selectedGroup || undefined, show);
	const {
		data: preview,
		isLoading: previewLoading,
		isFetching: previewFetching,
		isError: previewError,
	} = useSalesforceImportPreview(
		{
			onGroup: selectedGroup || undefined,
			onCampaignName: selectedCampaignName || undefined,
		},
		show
	);

	const isRowSelectable = useCallback(
		(row: SalesforceImportPreviewRow) =>
			(importFacebook && row.importableFacebook) ||
			(importGoogle && row.importableGoogle),
		[importFacebook, importGoogle]
	);
	const isRowDisabled = useCallback(
		(row: SalesforceImportPreviewRow) => !isRowSelectable(row),
		[isRowSelectable]
	);
	const previewColumns = usePreviewColumns({ isRowDisabled });
	const previewColumnProps = useColumnTemplate({
		columns: previewColumns,
		itemType: 'salesforce-import-preview',
		minSticky: 1,
	});
	const { selected: selectedRowIds, setSelected: setSelectedRowIds } =
		useDataTableMeta();

	useEffect(() => {
		setValue('onCampaignName', '');
	}, [selectedGroup, setValue]);

	// Selections are only valid for the currently previewed group/campaign
	useEffect(() => {
		setSelectedRowIds([]);
	}, [selectedGroup, selectedCampaignName, setSelectedRowIds]);

	const onClose = () => {
		formMethods.reset();
		setSelectedRowIds([]);
		setShow(false);
	};

	const selectedImportableRowIds = useMemo(
		() =>
			(preview?.rows ?? [])
				.filter(
					(row) => selectedRowIds.includes(row.id) && isRowSelectable(row)
				)
				.map((row) => row.id),
		[preview, selectedRowIds, isRowSelectable]
	);
	const hasSelectedRows = selectedRowIds.length > 0;

	const onSubmit = async (values: StartSalesforceImportBody) => {
		await startImport({
			...values,
			templateIds: hasSelectedRows ? selectedImportableRowIds : undefined,
		});
		onClose();
	};

	const missingPermission = ability.cannot('create', 'Salesforce Import');
	const hasImportableRows =
		preview?.rows.some(
			(row) =>
				(importFacebook && row.importableFacebook) ||
				(importGoogle && row.importableGoogle)
		) ?? false;
	const hasImportableSelection = hasSelectedRows
		? selectedImportableRowIds.length > 0
		: hasImportableRows;
	const importButtonDisabled =
		!selectedGroup ||
		!selectedCampaignName ||
		previewLoading ||
		previewFetching ||
		!hasImportableSelection;
	const previewRowStyle = useMemo(
		() => (row: SalesforceImportPreviewRow) =>
			isRowSelectable(row) ? undefined : styles.archived,
		[isRowSelectable]
	);
	const importTooltip = useMemo(() => {
		if (!selectedGroup || !selectedCampaignName) {
			return t('error.selectGroupAndCampaign');
		}

		if (previewError) {
			return t('error.loadingPreview');
		}

		if (!previewLoading && !previewFetching && preview?.rows.length === 0) {
			return t('error.noPreviewRows');
		}

		if (!previewLoading && !previewFetching && !hasImportableSelection) {
			return t('error.noImportableTemplates');
		}

		return undefined;
	}, [
		hasImportableSelection,
		preview,
		previewError,
		previewFetching,
		previewLoading,
		selectedCampaignName,
		selectedGroup,
		t,
	]);

	return (
		<FormProvider {...formMethods}>
			<HubTooltip
				title={
					missingPermission
						? missingPermissionText('create', 'Salesforce Import')
						: t('Salesforce import')
				}
			>
				<div
					onClick={() => {
						if (missingPermission) return;
						setShow(true);
					}}
					css={styles.importIconContainer(missingPermission)}
				>
					<FluentCloudDownload24Regular css={styles.importIcon} />
				</div>
			</HubTooltip>
			<ModalWithLayout
				show={show}
				header={
					<h1 css={styles.salesforceImportTitle}>{t('Salesforce import')}</h1>
				}
				body={
					<div css={styles.salesforceImportContainer}>
						{selectionOptionsError || campaignSelectionOptionsError ? (
							<div css={styles.errorText}>
								{t('error.loadingSelectionOptions')}
							</div>
						) : null}
						<div css={styles.selectorsContainer}>
							<HubAutocompleteWithControl
								name="onGroup"
								label={t('Group')}
								options={selectionOptions?.groups ?? []}
								isLoading={selectionOptionsLoading}
							/>
							<HubAutocompleteWithControl
								name="onCampaignName"
								label={t('Campaign name')}
								options={campaignSelectionOptions?.campaignNames ?? []}
								disabled={!selectedGroup}
								isLoading={campaignSelectionOptionsLoading}
							/>
						</div>

						<div css={styles.previewCard}>
							<div css={styles.previewHeader}>
								<div
									css={{
										display: 'flex',
										alignItems: 'center',
										gap: '0.5rem',
									}}
								>
									<div css={styles.previewTitle}>{t('Preview')}</div>
									<InfoCircle calloutText={t('preview.info')} />
								</div>
								{preview ? (
									<div css={styles.previewSummary}>
										{t('preview.summary', {
											count: preview.rows.length,
											facebookCount: preview.importableFacebookCount,
											googleCount: preview.importableGoogleCount,
										})}
									</div>
								) : null}
							</div>
							{!selectedGroup || !selectedCampaignName ? (
								<div css={styles.previewEmptyState}>
									{t('preview.selectFields')}
								</div>
							) : previewError ? (
								<div css={[styles.previewEmptyState, styles.errorText]}>
									{t('error.loadingPreview')}
								</div>
							) : previewLoading || previewFetching ? (
								<div css={styles.previewLoading}>
									<LoadingIndicator />
								</div>
							) : !preview || preview.rows.length === 0 ? (
								<div css={styles.previewEmptyState}>{t('preview.empty')}</div>
							) : (
								<div css={styles.previewTableWrap}>
									<DataTableFull
										{...previewColumnProps}
										data={preview.rows}
										itemType="salesforce-import-preview"
										rowStyle={previewRowStyle}
										hideColumnTemplateButton
									/>
								</div>
							)}
						</div>
					</div>
				}
				footer={
					<ButtonRow
						right={
							<>
								<ButtonCancel onClick={onClose}>{t('Cancel')}</ButtonCancel>
								<HubTooltip title={importTooltip}>
									<ButtonWithLoading
										loading={importLoading}
										onClick={handleSubmit(onSubmit)}
										variant="contained"
										disabled={importButtonDisabled}
									>
										{t('Import')}
									</ButtonWithLoading>
								</HubTooltip>
							</>
						}
					/>
				}
			/>
		</FormProvider>
	);
}
